import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import {
  Clock, Shield, Headphones, Zap, Globe, TrendingUp,
  Truck, MapPin, Package, Phone, ArrowRight, Users, BarChart3,
  FileCheck, Radio, Send, CheckCircle2
} from 'lucide-react';

const marqueeItems = [
  'MC-1394566', 'DOT-3837808', '2,500+ CARRIERS', '48 STATES',
  '99.8% ON-TIME', 'FMCSA COMPLIANT', '$100K CARGO INSURANCE',
  '$1M GENERAL LIABILITY', '24/7 DISPATCH',
];

// ==================== ANIMATED COUNTER ====================
const Counter = ({ target, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const num = parseFloat(String(target).replace(/[^0-9.]/g, ''));
    if (isNaN(num)) { setCount(target); return; }
    const duration = 2000;
    const steps = 60;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) { setCount(num); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {prefix}{typeof count === 'number' ? count.toLocaleString() : count}{suffix}
    </span>
  );
};

// ==================== MARQUEE BAR ====================
export const TrustMarquee = () => (
  <div className="bg-red-600 py-3.5 overflow-hidden">
    <Marquee speed={60} gradient={false}>
      {marqueeItems.map((item) => (
        <span key={item} className="text-white text-sm font-bold uppercase tracking-wider mx-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {item}
          <span className="text-red-300 mx-8">&#x2022;</span>
        </span>
      ))}
    </Marquee>
  </div>
);

// ==================== NUMBERS-DRIVEN PROOF ====================
export const NumbersProof = () => {
  const stats = [
    { value: 2500, suffix: '+', label: 'Partner Trucks', sub: 'for surge & specialized capacity' },
    { value: 15000, suffix: '+', label: 'Loads Delivered', sub: 'and counting' },
    { value: 48, suffix: '', label: 'States Covered', sub: 'coast to coast' },
    { value: 99.8, suffix: '%', label: 'On-Time Rate', sub: 'industry-leading reliability' },
  ];

  return (
    <section className="py-20 bg-zinc-950 border-b border-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="bg-zinc-900 border border-zinc-800 p-6 sm:p-8 text-center hover:border-zinc-700 transition-colors"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="text-4xl sm:text-5xl font-black text-white tracking-tighter mb-1">
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div className="text-sm font-bold text-red-500 uppercase tracking-wider">{s.label}</div>
              <div className="text-xs text-zinc-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== DUAL-AUDIENCE PATHWAYS ====================
export const DualAudience = () => (
  <section className="bg-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
      <div className="text-center mb-14">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">Two Audiences, One Platform</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          Whether You Ship or Haul — We've Got You
        </h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Shippers */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden min-h-[420px] group"
          data-testid="pathway-shippers"
        >
          <img src="https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Warehouse" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
          <div className="relative z-10 flex flex-col justify-end h-full p-8 sm:p-10">
            <span className="text-red-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">For Shippers</span>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">Move Your Freight with Confidence</h3>
            <p className="text-zinc-400 mb-6 max-w-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Competitive rates, real-time tracking, and a dedicated agent who knows your lanes. Get capacity when you need it — even in tight markets.
            </p>
            <ul className="space-y-2 mb-8">
              {['Instant freight quotes', 'Dedicated account management', 'Real-time GPS tracking', 'Claims support & insurance'].map(item => (
                <li key={item} className="flex items-center gap-2 text-zinc-300 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/quote"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 font-bold uppercase tracking-wider text-sm w-fit transition-all hover:-translate-y-0.5"
              data-testid="shipper-cta-button"
            >
              Get a Free Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>

        {/* Carriers */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden min-h-[420px] group"
          data-testid="pathway-carriers"
        >
          <img src="https://images.pexels.com/photos/10359129/pexels-photo-10359129.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Trucks on highway" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
          <div className="relative z-10 flex flex-col justify-end h-full p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-3">
              <Radio className="w-3 h-3 text-red-500 animate-pulse" />
              <span className="text-red-500 text-xs font-bold uppercase tracking-[0.2em]">For Carriers</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">Find Loads. Get Paid Fast.</h3>
            <p className="text-zinc-400 mb-6 max-w-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Browse our live load board, get matched to loads in your lanes, and enjoy competitive rates with prompt payment on every load.
            </p>
            <ul className="space-y-2 mb-8">
              {['Live load board — updated every 2 min', 'Competitive per-mile rates', 'Quick pay available', 'Nationwide lanes across 48 states'].map(item => (
                <li key={item} className="flex items-center gap-2 text-zinc-300 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/load-board"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 font-bold uppercase tracking-wider text-sm w-fit transition-all hover:-translate-y-0.5"
              data-testid="carrier-cta-button"
            >
              View Available Loads <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// ==================== HOW IT WORKS ====================
const steps = [
  { num: '01', icon: Send, title: 'Request a Quote', desc: 'Tell us your origin, destination, and freight details. Get a competitive rate in minutes — not hours.' },
  { num: '02', icon: Users, title: 'We Dispatch a Truck', desc: 'Our dispatch team assigns your load to one of our own trucks — or, for surge/specialty freight, one of our 2,500+ vetted partner trucks.' },
  { num: '03', icon: MapPin, title: 'Track in Real-Time', desc: 'Follow your shipment mile-by-mile with GPS tracking. Get live ETAs and proactive status updates.' },
  { num: '04', icon: FileCheck, title: 'Delivery Confirmed', desc: 'Signed BOL, POD, and invoice — all handled. Your freight arrives on time, every time.' },
];

export const HowItWorks = () => (
  <section className="py-24 bg-zinc-950 border-t border-zinc-900 relative overflow-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/67b0925b-7f4b-4cc2-900d-1c317dc2d0bd/images/4dd55f99d4976c4423d32e3460eac68a307532bc5387b921748bec70905a673c.png')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">Simple Process</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          From Quote to Delivery in 4 Steps
        </h2>
        <p className="text-zinc-500 mt-4 max-w-xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          No complexity. No runaround. Just freight that moves.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((step, i) => (
          <motion.div
            key={step.num}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15 }}
            className="relative bg-zinc-900/80 backdrop-blur border border-zinc-800 p-8 hover:border-red-600/30 transition-all duration-300 group"
            data-testid={`step-${step.num}`}
          >
            <div className="text-5xl font-black text-zinc-800 group-hover:text-zinc-700 transition-colors absolute top-4 right-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {step.num}
            </div>
            <div className="w-12 h-12 bg-red-600/10 flex items-center justify-center mb-5 group-hover:bg-red-600/20 transition-colors">
              <step.icon className="w-6 h-6 text-red-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
            <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{step.desc}</p>
          </motion.div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link
          to="/quote"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all hover:-translate-y-0.5"
          data-testid="process-cta-button"
        >
          Start Step 1 — Get Your Quote <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

// ==================== BENTO FEATURES ====================
const features = [
  { icon: Zap, title: 'Same-Day Dispatch', description: 'Post your load and get a carrier assigned within hours. Our network of 2,500+ carriers is always moving.', span: 'md:col-span-2' },
  { icon: Shield, title: 'Fully Insured', description: '$100K cargo + $1M general liability on every single load. Zero exceptions.', span: 'md:col-span-1' },
  { icon: Headphones, title: '24/7 Live Dispatch', description: 'Real dispatchers, not chatbots. Call us at 2 AM on Christmas — someone answers in under 30 seconds.', span: 'md:col-span-1' },
  { icon: Globe, title: '48-State Coverage', description: 'From Port of Long Beach to Port of Savannah. Every major lane in the continental US, covered.', span: 'md:col-span-1' },
  { icon: TrendingUp, title: 'No Hidden Fees', description: 'Transparent, market-rate pricing. The rate we quote is the rate you pay. Period.', span: 'md:col-span-1' },
  { icon: BarChart3, title: 'Real-Time GPS Tracking', description: 'Know where your freight is at every mile. Live ETAs, driver check-calls, and proactive exception alerts.', span: 'md:col-span-2' },
];

export const BentoFeatures = () => (
  <section className="py-24 bg-zinc-950 border-t border-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">The Red Ball Advantage</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          Built for Speed. Backed by Trust.
        </h2>
        <p className="text-zinc-500 mt-4 max-w-2xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Every trucking company promises reliability. We prove it — with our own fleet, our own drivers, and 24/7 dispatch that never sleeps.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className={`bg-zinc-900 border border-zinc-800 p-8 hover:border-zinc-700 hover:shadow-lg hover:shadow-red-600/5 transition-all duration-300 group ${f.span}`}
          >
            <div className="w-12 h-12 bg-red-600/10 flex items-center justify-center mb-5 group-hover:bg-red-600/20 transition-colors">
              <f.icon className="w-6 h-6 text-red-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
            <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{f.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
